/*
 * File: useLeftyTheSalesmanService.ts
 * Project: papa-bear
 * -----
 * Last Modified: 01 Dec 2021 11:56:17 am
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import { SalespersonModel } from 'models/SalespersonModel';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
export interface GetEmployeesParams {
  dataAreaId?: string;
  employeeNameContains?: string;
  overrideSkipTake: boolean;
  offset: number;
  employeeErpId?: string;
}

export interface GetEmployeesResponseModel {
  data: SalespersonModel[];
  totalCount: number;
}

/* ****************** Variables ****************** */
const leftyTheSalesmanBaseUrl = process.env.REACT_APP_LEFTY_THE_SALESMAN_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_LEFTY_THE_SALESMAN;

const baseQuery = fetchBaseQuery({
  baseUrl: leftyTheSalesmanBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

/* ******************** Entity Adapter ******************** */
// const salesPersonAdapter = createEntityAdapter<SalespersonModel>({
//   selectId: (employee) => employee.personnelNumber,
//   sortComparer: (a, b) => a.name.localeCompare(b.name)
// });
// const initialState = salesPersonAdapter.getInitialState();

export const salespersonApi = createApi({
  reducerPath: 'salespersonApi',
  baseQuery,
  tagTypes: ['Employees'],
  endpoints: (builder) => ({
    getEmployeesByName: builder.query<GetEmployeesResponseModel, GetEmployeesParams>({
      query: ({ employeeNameContains, offset, dataAreaId, overrideSkipTake }) => {
        return {
          url: 'employees',
          params: {
            dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
            employeeNameContains: employeeNameContains ? encodeValue({ isUrl: false, value: employeeNameContains } ?? '') : undefined,
            overrideSkipTake,
            offset
          }
        };
      },
      providesTags: (result) => (result ? [...result.data.map(({ personnelNumber }) => ({ type: 'Employees' as const, id: personnelNumber }))] : ['Employees'])
    }),
    getEmployees: builder.query<GetEmployeesResponseModel, GetEmployeesParams>({
      query: ({ dataAreaId, offset, overrideSkipTake, employeeErpId, employeeNameContains }) => ({
        url: `employees`,
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
          overrideSkipTake,
          offset,
          employeeErpId,
          employeeNameContains
        }
      })
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetEmployeesQuery, useLazyGetEmployeesQuery, useGetEmployeesByNameQuery, useLazyGetEmployeesByNameQuery, useGetVersionQuery, useGetApiNameQuery } = salespersonApi;
